import { useSearchParams } from "react-router-dom";
import { Printer } from "lucide-react";
import { useApi } from "../lib/api";
import { cn } from "../lib/utils";
import { eur, fmtLong, num, todayISO } from "../lib/format";

const td = "border border-black px-2 py-1.5 align-top";
const MODES = [["cantiere", "Per cantiere + riepilogo operai"], ["operaio", "Foglio per operaio"]];

function SiteSheet({ r }) {
  return (
    <div className="mb-8" style={{ breakInside: "avoid" }}>
      <div className="flex justify-between font-bold text-lg"><span>{r.site.code} · CANTIERE {r.site.name.toUpperCase()}</span><span className="text-sm font-normal">Resp.: {r.site.manager_name}</span></div>
      <div className="text-xs mb-2">{r.site.address}</div>
      <table className="w-full text-sm border-collapse">
        <thead><tr className="bg-gray-100">{["#", "Operaio", "Qualifica", "Orario", "Ore", "Entrata", "Uscita", "Firma"].map((h) => <th key={h} className={`${td} text-left`}>{h}</th>)}</tr></thead>
        <tbody>
          {r.workers.map((w, i) => (
            <tr key={w.assignment_id}><td className={td}>{i + 1}</td><td className={td}>{w.name}</td><td className={td}>{w.qualification}</td>
              <td className={td}>{w.shift}</td><td className={td}>{num(w.hours)}</td><td className={`${td} w-20`} /><td className={`${td} w-20`} /><td className={`${td} w-32`} /></tr>
          ))}
        </tbody>
      </table>
      {r.vehicles.length > 0 && <div className="text-xs mt-2"><b>Mezzi:</b> {r.vehicles.map((v) => v.label).join(" · ")}</div>}
      <div className="text-xs mt-1">Ore totali: <b>{num(r.hours)}</b> · Costo manodopera: <b>{eur(r.labor_cost)}</b> · Costo mezzi: <b>{eur(r.vehicle_cost)}</b> · Totale: <b>{eur(r.total_cost)}</b></div>
    </div>
  );
}

function WorkerSummary({ workers }) {
  return (
    <div style={{ breakBefore: "page" }} className="pt-6" data-testid="print-worker-summary">
      <div className="font-bold text-lg mb-2">RIEPILOGO PER OPERAIO</div>
      <table className="w-full text-sm border-collapse">
        <thead><tr className="bg-gray-100">{["#", "Operaio", "Cantieri e orari", "Ore totali"].map((h) => <th key={h} className={`${td} text-left`}>{h}</th>)}</tr></thead>
        <tbody>
          {workers.map((w, i) => (
            <tr key={w.worker_id} style={{ breakInside: "avoid" }} data-testid={`print-summary-worker-${w.worker_id}`}>
              <td className={td}>{i + 1}</td><td className={td}><b>{w.name}</b><div className="text-xs">{w.qualification}</div></td>
              <td className={td}>{w.slots.map((s) => <div key={s.assignment_id}><b>{s.shift}</b> · {s.site_code} {s.site_name} ({num(s.hours)} h)</div>)}</td>
              <td className={`${td} font-bold`}>{num(w.hours)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkerRows({ w, n }) {
  return w.slots.map((s, i) => (
    <tr key={s.assignment_id} style={{ breakInside: "avoid" }} data-testid={`print-worker-slot-${s.assignment_id}`}>
      {i === 0 && <td className={td} rowSpan={w.slots.length}>{n}</td>}
      {i === 0 && <td className={td} rowSpan={w.slots.length}><b>{w.name}</b><div className="text-xs">{w.qualification} · {num(w.hours)} h totali</div></td>}
      <td className={td}>{s.site_code} {s.site_name}</td><td className={td}>{s.shift}</td><td className={td}>{num(s.hours)}</td>
      <td className={`${td} w-20`} /><td className={`${td} w-20`} /><td className={`${td} w-32`} />
    </tr>
  ));
}

function WorkerSheet({ workers }) {
  return (
    <table className="w-full text-sm border-collapse" data-testid="print-worker-sheet">
      <thead><tr className="bg-gray-100">{["#", "Operaio", "Cantiere", "Orario", "Ore", "Entrata", "Uscita", "Firma"].map((h) => <th key={h} className={`${td} text-left`}>{h}</th>)}</tr></thead>
      <tbody>{workers.map((w, i) => <WorkerRows key={w.worker_id} w={w} n={i + 1} />)}</tbody>
    </table>
  );
}

function Toolbar({ mode, setMode }) {
  return (
    <div className="no-print sticky top-0 bg-slate-900 text-white p-3 flex flex-wrap gap-2 justify-between items-center">
      <div className="flex flex-wrap gap-1">
        {MODES.map(([k, l]) => (
          <button key={k} onClick={() => setMode(k)} className={cn("px-3 py-1.5 rounded-md text-sm border", mode === k ? "bg-white text-slate-900 border-white" : "border-slate-600")} data-testid={`print-mode-${k}`}>{l}</button>
        ))}
      </div>
      <button onClick={() => window.print()} className="bg-amber-500 text-slate-950 font-bold px-4 py-2 rounded-md flex items-center gap-2" data-testid="print-now-btn"><Printer className="h-4 w-4" />Stampa / PDF</button>
    </div>
  );
}

function PrintBody({ data, mode }) {
  const byWorker = mode === "operaio";
  const hasWorkers = data.workers.length > 0;
  return (
    <>
      {data.off_reason && <p className="mb-4 font-semibold" data-testid="print-off-day">Giorno non lavorativo ({data.off_reason}): ore e costi non conteggiati.</p>}
      {!data.sites.length && <p>Nessuna presenza pianificata per questa data.</p>}
      {!byWorker && data.sites.map((r) => <SiteSheet key={r.site.id} r={r} />)}
      {!byWorker && hasWorkers && <WorkerSummary workers={data.workers} />}
      {byWorker && hasWorkers && <WorkerSheet workers={data.workers} />}
    </>
  );
}

export default function PrintSheet() {
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || todayISO();
  const mode = params.get("mode") === "operaio" ? "operaio" : "cantiere";
  const setMode = (m) => setParams({ date: day, mode: m });
  const { data } = useApi("/operations", { day });
  if (!data) return <div className="p-10 text-center">Caricamento…</div>;
  return (
    <div className="min-h-screen bg-white text-black print-page" style={{ backgroundImage: "none" }}>
      <Toolbar mode={mode} setMode={setMode} />
      <div className="max-w-[900px] mx-auto p-8" data-testid="print-sheet">
        <div className="flex justify-between items-end border-b-2 border-black pb-3 mb-6">
          <div><div className="text-2xl font-extrabold font-heading">FOGLIO PRESENZE{mode === "operaio" && " PER OPERAIO"}</div><div className="text-sm">{data.company} · CANTIERE MANAGER PRO</div></div>
          <div className="text-right text-sm capitalize">{fmtLong(day)}</div>
        </div>
        <PrintBody data={data} mode={mode} />
        <div className="mt-12 grid grid-cols-2 gap-10 text-sm" style={{ breakInside: "avoid" }}><div className="border-t border-black pt-1">Firma responsabile</div><div className="border-t border-black pt-1">Firma direzione lavori</div></div>
      </div>
    </div>
  );
}
